import { Injectable } from '@angular/core';
import {
  Catalog,
  COUNTRY_CODE,
  OrderStatusEnum,
  OrderTypeCodesEnum,
  RmsOrderRequest,
  SelectedAddedValuesRms,
} from '@cemex-core/cmx-api';
import { getAuth, getLanguage, getLegalEntity } from '@cemex/cmx-client-layout';
import { createSelector, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getOrderCode } from '@ordersnproducts/modules/new-modify-order/reducers';
import {
  getModifyReasonCode,
  getModifyReasonComment,
} from '@ordersnproducts/modules/new-modify-order/selectors/modify-reason.selector';
import {
  getBusinessSelectedStates,
  getJobsiteSelected,
  getLocationSelectedStates,
  getPurchaseOrderSelected,
  getSalesAreas,
  getShimpmentLocations,
  getSpecialInstructionsSelected,
} from '@ordersnproducts/modules/order-builder/modules/review/reducers/review.selectors';
import {
  getProductRmsSpecifications,
  getRmsRecipeSpecifications,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import {
  getAddedValuesState,
  productAddedValues,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/added-value.selectors';
import { getAvailableContracts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/contracts.selectors';
import { getAvailableProducts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';
import {
  getSlotAvailable,
  getSlotError,
  getSlotsAvailability,
  getSlotsResponse,
  getSlotWaitingListDefaulted,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/slots.selectors';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import {
  getCatalogsSelector,
  getFirstCalendar,
  getLocationPage,
  getSummaryPage,
  OrderBuilderState,
} from '@ordersnproducts/modules/order-builder/reducers';
import { ADDITIONAL_PARAMETERS } from '@ordersnproducts/modules/shared/constants/additional-parameters';
import { CATALOG_CODE } from '@ordersnproducts/modules/shared/constants/catalog-codes';
import { DIVISION_CODE } from '@ordersnproducts/modules/shared/constants/division-codes';
import { Mark } from '@ordersnproducts/modules/shared/constants/mark';
import { OrderCategory } from '@ordersnproducts/modules/shared/models/order-category.enum';
import { ErrorCode } from '@ordersnproducts/modules/shared/models/slots-error-code.enum';
import { calculateAdjustmentQuantity } from '@ordersnproducts/modules/shared/utils/adjustment-quantity';
import { isPump, isCemexPump } from '@ordersnproducts/modules/shared/utils/pump';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { divisionSalesArea } from '@ordersnproducts/modules/shared/utils/sales-area-by-division.method';
import { environment } from 'environments/environment';
import * as moment from 'moment';
import { of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { BOOM_PUMP, BOOM_PUMP_CODE, isApiPumpType } from '../../rms-specification/constants/pumps';
import { findAddedValuesFromIds } from '../../rms-specification/helpers/find-added-values-inside-recommendations';
import { getRmsModifyFlag } from '../../rms-specification/selectors/rms-modify-flag.selectors';
import { RmsConfigLoaderService } from '../../rms-specification/services/config-loader/rms-config.loader.service';
import { KeyValues } from '../../specification/models/specification-form-values';

const DIRECT_FLOW = 'DIRECT FLOW';

const addedValuesAndProducts = createSelector(
  getAddedValuesState,
  getAvailableProducts,
  (addedValuesState, availableProducts) => {
    return { addedValuesState, availableProducts };
  },
);

/**
 * Solves the Rms request
 */
@Injectable()
export class RmsOrderRequestSolverService {
  constructor(
    private store: Store<OrderBuilderState>,
    private translateService: TranslateService,
    private configService: RmsConfigLoaderService,
  ) {}

  public createRmsBlankRequest() {
    const createRmsOrderRequest = {
      salesDocument: '',
      status: '',
      testRun: '',
      systemSource: '',
      interfaceId: '',
      origin: 'CGSO',
      salesOrg: '',
      distChannel: '',
      division: '',
      shippingCondition: '',
      username: '',
      purchaser: '',
      receiver: '',
      reqDate: '',
      reqTime: '',
      date: '',
      time: '',
      wTimeList: '',
      selectedJobsite: {},
      paymentTerm: '',
      purchaseOrder: '',
      contract: '',
      contractItem: '',
      specialInstructions: '',
      plant: '',
      selectedP: '',
      selectedT: '',
      quantity: '',
      capacity: '',
      spacing: 0,
      application: '',
      unloadType: {},
      adjustment: '',
      adjustmentVolume: '',
      selectedValueAddeds: [],
      pumpContract: '',
      pumpContractItem: '',
      pumpSpecialInstructions: '',
      information: '',
      custyCRM: '',
      fuente: '06',
      OT: '',
      jobsite: '',
      pumpSizeCode: '',
      pumpAssign: '',
      pumpId: '',
      washOnSite: '',
      pumpStartDate: '',
      pumpStart: '',
      pumpFinishDate: '',
      pumpFinish: '',
      installTime: 0,
      pipes: 0,
      hoses: 0,
      crew: 1,
      orderType: '',
      modReason: null,
      comments: '',
      description: '',
      category: '',
      slump: 0,
    };

    return createRmsOrderRequest as RmsOrderRequest;
  }

  async createRmsOrderRequest() {
    const createRmsOrderRequest = {
      salesDocument: await this.getOrderCode(),
      status: await this.getOrderStatus(),
      testRun: '',
      systemSource: await this.getApplicationCode(),
      interfaceId: '',
      origin: 'CGSO',
      salesOrg: await this.getSalesOrganizationCode(),
      distChannel: await this.getDistributionChannel(),
      division: await this.getDivision(),
      shippingCondition: await this.getShippingConditionCode(),
      username: '',
      purchaser: await this.getPurchaser(),
      receiver: await this.getReceivingPerson(),
      reqDate: await this.getRequestedDate(),
      reqTime: await this.getRequestedTime(),
      date: await this.getScheduleDate(),
      time: await this.getScheduleTime(),
      wTimeList: await this.getWaitingListMark(),
      selectedJobsite: await this.getJobsiteSelected(),
      paymentTerm: await this.getPaymentTerm(),
      purchaseOrder: await this.getPurchaseOrder(),
      contract: await this.getContractCode(),
      contractItem: await this.getContractItem(),
      specialInstructions: await this.getSpecialInstructions(),
      plant: await this.getPlant(),
      selectedP: await this.getProductCode(),
      selectedT: await this.technicalProduct(),
      quantity: await this.getQuantity(),
      capacity: await this.getCapacity(),
      spacing: await this.getSpacing(),
      application: await this.getApplication(),
      unloadType: await this.getUnloadType(),
      adjustment: '',
      adjustmentVolume: await this.getAdjustmentQuantity(),
      selectedValueAddeds: await this.addedValues(),
      pumpContract: '',
      pumpContractItem: '',
      pumpSpecialInstructions: '',
      information: '',
      custyCRM: '',
      fuente: '06',
      OT: '',
      jobsite: await this.getJobsiteCode(),
      pumpSizeCode: await this.getPumpSize(),
      pumpAssign: await this.getPump(),
      pumpId: await this.getPumpId(),
      washOnSite: await this.getCleanUp(),
      pumpStartDate: await this.getStartDate(),
      pumpStart: await this.getStartTime(),
      pumpFinishDate: await this.getEndDate(),
      pumpFinish: await this.getEndTime(),
      installTime: await this.getInstallationTime(),
      pipes: await this.getPipesQuantity(),
      hoses: await this.getHosesQuantity(),
      crew: 0,
      orderType: await this.getDocumentType(),
      langCode: await this.getLanguage(),
      modReason: await this.getModifyReasonCode(),
      comments: await this.getModifyReasonComment(),
      description: await this.getModifyReasonComment(),
      category: await this.getOrderCategory(),
      slump: await this.getSlump(),
    };

    const orderCode = await this.getOrderCode();
    createRmsOrderRequest['dcoId'] = await this.getDigitalConfirmationTicket();
    createRmsOrderRequest['dcoExecutionTime'] = await this.getDigitalConfirmationRunTime();
    if (orderCode === '') {
      createRmsOrderRequest['uniqueOrderReqId'] = this.generateMagicNumber();
    } else {
      createRmsOrderRequest['uniqueOrderReqId'] = '';
    }

    // Special case for boom pump specific size
    const reqLengthPump = await this.getReqPumpType();
    if (reqLengthPump) {
      createRmsOrderRequest['reqPumpType'] = reqLengthPump - 1;
      createRmsOrderRequest['maxPumpType'] = reqLengthPump;
    }

    // Case for linear meters
    const linearMeters = await this.getLinearMeters();
    if (linearMeters !== undefined) {
      createRmsOrderRequest['linearMts'] = linearMeters;
      createRmsOrderRequest.pipes = Math.ceil(linearMeters / 3);
    }

    return createRmsOrderRequest as unknown as RmsOrderRequest;
  }

  private async getApplicationCode() {
    return this.store
      .select(getLegalEntity)
      .pipe(
        take(1),
        map((customer) => environment.applicationCode[customer.countryCode]),
      )
      .toPromise();
  }

  private async getCountryCode() {
    return this.store
      .select(getLegalEntity)
      .pipe(
        take(1),
        map((customer) => customer.countryCode),
      )
      .toPromise();
  }

  private async getOrderCode() {
    return this.store
      .select(getOrderCode)
      .pipe(
        take(1),
        map((orderCode) => {
          if (!!orderCode) {
            return orderCode;
          } else {
            return '';
          }
        }),
      )
      .toPromise();
  }

  /**
   * @returns Form recipe
   */
  private async recipe() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
      )
      .toPromise();
  }

  /**
   * @returns true if pump was selected
   */
  private async hasCemexPump() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          const unloadTypeValue = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
          const hasPump = isCemexPump(unloadTypeValue);
          return !!hasPump;
        }),
      )
      .toPromise();
  }

  private async getSalesOrganizationCode() {
    return this.store
      .select(getLocationPage)
      .pipe(
        map((state) =>
          _safe(() => state.jobsite.address.countryCode) === COUNTRY_CODE.USA
            ? '2037'
            : _safe(() => state.jobsite.jobsiteFullSalesArea.salesOrganizationCode.replace(/^0+/, '')),
        ),
        take(1),
      )
      .toPromise();
  }

  private async getJobsiteCode() {
    return this.store
      .select(getJobsiteSelected)
      .pipe(
        take(1),
        map((jobsite) => jobsite.shipmentLocationCode),
      )
      .toPromise();
  }

  private async getCustomer() {
    return this.store
      .select(getLegalEntity)
      .pipe(
        take(1),
        map((customer) => customer),
      )
      .toPromise();
  }

  private async getJobsiteSelected() {
    return this.store
      .select(getShimpmentLocations)
      .pipe(
        take(1),
        map(async (location) => {
          const additionalParameters = await this.getCustomer();
          return {
            partnerCode: Number(additionalParameters.legalEntityTypeCode),
            jobsiteCode: Number(_safe(() => location.jobsite.shipmentLocationCode)),
            POD: Number(_safe(() => location.pointOfDelivery.shipmentLocationCode)),
          };
        }),
      )
      .toPromise();
  }

  private async getCapacity() {
    return this.store
      .select(getFirstCalendar)
      .pipe(
        take(1),
        map((state) => state.loadSize.toString()),
      )
      .toPromise();
  }

  private async getDistributionChannel() {
    const country = await this.getCountryCode();
    if (country === COUNTRY_CODE.USA) {
      return '01';
    } else {
      return this.store
        .select(getSalesAreas)
        .pipe(
          take(1),
          map((salesAreas) => {
            const salesArea = divisionSalesArea(salesAreas, DIVISION_CODE.CEMENT);
            return salesArea.salesArea.channelCode;
          }),
        )
        .toPromise();
    }
  }

  private async getDivision() {
    const country = await this.getCountryCode();
    if (country === COUNTRY_CODE.USA) {
      return 10;
    } else {
      return this.store
        .select(getSalesAreas)
        .pipe(
          take(1),
          map((salesAreas) => {
            const salesArea = divisionSalesArea(salesAreas, DIVISION_CODE.CEMENT);
            return salesArea.salesArea.divisionCode;
          }),
        )
        .toPromise();
    }
  }

  private async getReceivingPerson() {
    return this.store
      .select(getLocationSelectedStates)
      .pipe(
        take(1),
        map((state) => {
          return state.recievingPerson ? `${state.recievingPerson.name}/${state.recievingPerson.phone}` : '';
        }),
      )
      .toPromise();
  }

  private async getPurchaser() {
    return this.store
      .select(getAuth)
      .pipe(
        take(1),
        map((auth) => {
          return auth.profile.userAccount;
        }),
      )
      .toPromise();
  }

  private async getPurchaseOrder() {
    return this.store
      .select(getPurchaseOrderSelected)
      .pipe(
        take(1),
        map((purchaseOrder) => {
          return purchaseOrder;
        }),
      )
      .toPromise();
  }

  private async getSpecialInstructions() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const specialInstructionFromForm = _safe(() => recipe[SpecFieldNames.SPECIAL_INSTRUCTION]);
          const additionalInformation = _safe(() => recipe[SpecFieldNames.ADDITIONAL_INFO]);
          return this.store.select(getSpecialInstructionsSelected).pipe(
            map((specialInstruction) => specialInstruction),
            map((specialInstruction) => {
              return !!specialInstructionFromForm ? specialInstructionFromForm : specialInstruction;
            }),
            map((specialInstrucctions) => {
              const instructions =
                specialInstrucctions === null || specialInstrucctions === undefined ? '' : specialInstrucctions;
              const information =
                additionalInformation === null || additionalInformation === undefined ? '' : additionalInformation;
              return instructions !== '' || information !== '' ? instructions + ' | ' + information : '';
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getPaymentTerm() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        map((recipe) => _safe(() => recipe[SpecFieldNames.PAYMENT_TERMS])),
      )
      .toPromise();
  }

  private async getShippingConditionCode() {
    return this.store
      .select(getBusinessSelectedStates)
      .pipe(
        take(1),
        map((state) => {
          return _safe(() => state.shippingCondition.shippingConditionCode);
        }),
      )
      .toPromise();
  }

  private async getProductCode() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const productCode = _safe(() => recipe[SpecFieldNames.PRODUCT_SELECTION]);
          return this.store.select(getAvailableProducts).pipe(
            map((availableProducts) =>
              _safe(
                () =>
                  availableProducts.find(
                    (product) => product.comm_product.comm_product.replace(/^0+/, '') === productCode,
                  ).comm_product.comm_product,
              ),
            ),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getContractCode() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const contractId = _safe(() => recipe[SpecFieldNames.CONTRACT_SELECT]);
          const productCode = _safe(() => recipe[SpecFieldNames.PRODUCT_SELECTION]);
          return this.store.select(getAvailableContracts).pipe(
            map((availableContracts) => availableContracts[productCode]),
            map((contracts) =>
              _safe(
                () =>
                  contracts.find((contract) => contract.documentCode.replace(/^0+/, '') === contractId).documentCode,
              ),
            ),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getContractItem() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const contractCode = _safe(() => recipe[SpecFieldNames.CONTRACT_SELECT]);
          const productCode = _safe(() => recipe[SpecFieldNames.PRODUCT_SELECTION]);
          return this.store.select(getAvailableContracts).pipe(
            map((availableContracts) => availableContracts[productCode]),
            map((contracts) =>
              _safe(() =>
                contracts
                  .find((contract) => contract.documentCode.replace(/^0+/, '') === contractCode)
                  .refDocumentItemNumber.toString(),
              ),
            ),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getSpacing() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getCatalogsSelector).pipe(
            map((catalogs) => {
              const spacingSelected = _safe(() => recipe[SpecFieldNames.SPACING]);
              const spacingCatalog = _safe(() =>
                catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.TIME_PER_LOAD),
              );
              const spacingAdditionalParameters = _safe(
                () => spacingCatalog.entries.find((entry) => entry.entryCode === spacingSelected).additionalParameters,
              );
              const spacingInMinutes = Number(
                _safe(
                  () =>
                    spacingAdditionalParameters.find(
                      (item) => item.key === ADDITIONAL_PARAMETERS.TIME_PER_LOAD_IN_MINUTES,
                    ).value,
                ),
              );
              return spacingInMinutes;
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getApplication() {
    const country = await this.getCountryCode();
    if (country === COUNTRY_CODE.USA) {
      return this.getExtendedApplication();
    } else {
      return this.store
        .select(getRmsRecipeSpecifications)
        .pipe(
          take(1),
          map((form) => _safe(() => form[0].values)),
          switchMap((recipe) => {
            return this.store.select(getCatalogsSelector).pipe(
              map((catalogs) => {
                const applicationCode = _safe(() => recipe[SpecFieldNames.APPLICATION]);
                const applicationCatalog = _safe(() =>
                  catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.ELEMENT),
                );
                const applicationDescription = _safe(
                  () => applicationCatalog.entries.find((entry) => entry.entryCode === applicationCode).entryDesc,
                );
                return applicationDescription;
              }),
              take(1),
            );
          }),
        )
        .toPromise();
    }
  }

  private async getExtendedApplication() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getCatalogsSelector).pipe(
            map((catalogs) => {
              const applicationCode = _safe(() => recipe[SpecFieldNames.APPLICATION]);
              const applicationCatalog = _safe(() =>
                catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.ELEMENT),
              );
              const applicationDescription = _safe(() =>
                applicationCatalog.entries.find((entry) => entry.entryCode === applicationCode),
              );
              const applicationExtendedDescription = _safe(
                () =>
                  applicationDescription.additionalParameters.find(
                    (item) => item.key === ADDITIONAL_PARAMETERS.ELEMENT_SAP_CODE,
                  ).value,
              );
              return applicationExtendedDescription;
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getUnloadType() {
    const country = await this.getCountryCode();
    if (country === COUNTRY_CODE.USA) {
      return {
        id: 2,
        description: DIRECT_FLOW,
        unloadTime: 30,
      };
    } else {
      return this.store
        .select(getRmsRecipeSpecifications)
        .pipe(
          take(1),
          map((form) => _safe(() => form[0].values)),
          switchMap((recipe) => {
            // Default case
            return this.store.select(getCatalogsSelector).pipe(
              take(1),
              map((catalogs) => this.defaultUnloadObject(catalogs, recipe)),
            );
          }),
        )
        .toPromise();
    }
  }

  private async getInstallationTime() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getFirstCalendar).pipe(
            map((state) => {
              const pumpInstallTime = _safe(() => state.pump.pumpInstallTime);
              const pipeInstallTime = _safe(() => state.pump.pipeInstallTime);
              const pipeQuantity = recipe[SpecFieldNames.PIPE_QUANTITY];
              const hoseQuantity = recipe[SpecFieldNames.HOSES];
              if (
                _safe(() => recipe[SpecFieldNames.PUMP]) !== undefined &&
                pumpInstallTime !== undefined &&
                pipeInstallTime !== undefined
              ) {
                return pumpInstallTime + pipeInstallTime * (pipeQuantity + hoseQuantity);
              } else {
                return 30;
              }
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getPumpId() {
    const hasPump = await this.hasCemexPump();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getCatalogsSelector).pipe(
            take(1),
            map((catalogs) => {
              const pumpCapacityCatalog = _safe(() =>
                catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.PUMP_CAPACITY),
              );

              if (isApiPumpType(recipe[SpecFieldNames.UNLOAD_TYPE])) {
                // Boom pump or stationary pump case
                const additionalParameters =
                  pumpCapacityCatalog.entries.length && pumpCapacityCatalog.entries[0].additionalParameters;
                return Number(
                  _safe(() => additionalParameters.find((item) => item.key === ADDITIONAL_PARAMETERS.PUMP_ID).value),
                );
              }

              // Regular case
              const pumpCapatityCodeSelected = _safe(() => recipe[SpecFieldNames.PUMP]);
              const pumpCapacityAdditionalParameters = _safe(
                () =>
                  pumpCapacityCatalog.entries.find((entry) => entry.entryCode === pumpCapatityCodeSelected)
                    .additionalParameters,
              );
              const pumpdId = Number(
                _safe(
                  () =>
                    pumpCapacityAdditionalParameters.find((item) => item.key === ADDITIONAL_PARAMETERS.PUMP_ID).value,
                ),
              );

              if (hasPump) {
                return pumpdId;
              } else {
                return '';
              }
            }),
          );
        }),
      )
      .toPromise();
  }

  private async getPumpSize() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          // Api pump case
          if (isApiPumpType(recipe[SpecFieldNames.UNLOAD_TYPE])) {
            return of(+recipe[SpecFieldNames.PUMP]);
          }

          // Default case
          return this.store.select(getCatalogsSelector).pipe(
            take(1),
            map((catalogs) => this.makePumpSizeCode(catalogs, recipe)),
          );
        }),
      )
      .toPromise();
  }

  private async getPlant() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const dateTime = this.getDateTime(availableSlot, recipe);
          return this.store.select(getSlotsResponse).pipe(
            map(async (slots) => {
              const plant = _safe(
                () =>
                  slots.concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).plant,
              );
              const isBigOrder = await this.isBigOrder();
              const scheduleAvailability = await this.getScheduleAvailability();
              const foreignJobsites = await this.isForeignJobsites();
              return !!isBigOrder || !scheduleAvailability || !!foreignJobsites ? await this.defaultPlant() : plant;
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getStartDate() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const dateTime = this.getDateTime(availableSlot, recipe);
          return this.store.select(getSlotsResponse).pipe(
            map((slots) => {
              const firstUnldSlot = _safe(
                () =>
                  slots.concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).firstUnldSlot,
              );
              const deliveryDate = _safe(
                () => slots.pumpingProposals.find((proposal) => proposal.firstUnldSlot === firstUnldSlot).deliveryDate,
              );
              const unloadTypeValue = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
              const hasPump = isPump(unloadTypeValue);
              if (!!hasPump && deliveryDate !== undefined) {
                return deliveryDate;
              } else {
                return '';
              }
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getStartTime() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const dateTime = this.getDateTime(availableSlot, recipe);
          return this.store.select(getSlotsResponse).pipe(
            map((slots) => {
              const firstUnldSlot = _safe(
                () =>
                  slots.concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).firstUnldSlot,
              );
              const startTime = _safe(
                () => slots.pumpingProposals.find((proposal) => proposal.firstUnldSlot === firstUnldSlot).toSiteText,
              );
              const unloadTypeValue = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
              const hasPump = isPump(unloadTypeValue);
              if (!!hasPump && startTime !== undefined) {
                return startTime;
              } else {
                return '';
              }
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getAvailableSlotDate() {
    return this.store
      .select(getSlotAvailable)
      .pipe(
        take(1),
        map((response) => {
          return response;
        }),
      )
      .toPromise();
  }

  private async getSlotWaitingListDefaulted() {
    return this.store
      .select(getSlotWaitingListDefaulted)
      .pipe(
        take(1),
        map((response) => {
          return response;
        }),
      )
      .toPromise();
  }

  private getDateTime(availableSlot, recipe) {
    if (availableSlot === null) {
      return {
        date: moment(_safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT])).format('YYYY-MM-DD'),
        time: moment(_safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT])).format('HH:mm'),
      };
    } else {
      return {
        date: availableSlot.format('YYYY-MM-DD'),
        time: availableSlot.format('HH:mm'),
      };
    }
  }

  private async getEndDate() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const dateTime = this.getDateTime(availableSlot, recipe);
          return this.store.select(getSlotsResponse).pipe(
            map((slots) => {
              const loads = _safe(
                () =>
                  slots.concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).loads,
              );
              const unloadTypeValue = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
              const hasPump = isPump(unloadTypeValue);
              if (!!hasPump && loads !== undefined) {
                const lastLoad = loads[loads.length - 1];
                return moment(lastLoad.end).format('YYYY-MM-DD');
              } else {
                return '';
              }
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getEndTime() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const dateTime = this.getDateTime(availableSlot, recipe);
          return this.store.select(getSlotsResponse).pipe(
            map((slots) => {
              const loads = _safe(
                () =>
                  slots.concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).loads,
              );
              const unloadTypeValue = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
              const hasPump = isPump(unloadTypeValue);
              if (!!hasPump && loads !== undefined) {
                const lastLoad = loads[loads.length - 1];
                return moment(lastLoad.end).format('HH:mm');
              } else {
                return '';
              }
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getPump() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getProductRmsSpecifications)
      .pipe(
        take(1),
        switchMap((state) => {
          const recipe = state.form[0].values;
          const dateTime = this.getDateTime(availableSlot, recipe);
          const pumpCapacity = _safe(() => recipe[SpecFieldNames.PUMP]);
          return this.store.select(getSlotsResponse).pipe(
            map((slots) => {
              const firstUnldSlot = _safe(
                () =>
                  slots.concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).firstUnldSlot,
              );

              const pump = _safe(
                () => slots.pumpingProposals.find((proposal) => proposal.firstUnldSlot === firstUnldSlot).pump,
              );
              if (pumpCapacity && pump !== undefined) {
                return pump && pump.toString();
              } else {
                if (
                  recipe[SpecFieldNames.UNLOAD_TYPE] === 'PLACING_BOOM_PUMP' ||
                  recipe[SpecFieldNames.UNLOAD_TYPE] === 'STATIONARY_PUMP'
                ) {
                  return _safe(() =>
                    state.orderSelectDetail.order.items[0].orderItemProfile.pumpCapacity['pumpAssig'].replace(
                      /^0+/,
                      '',
                    ),
                  );
                } else {
                  return '';
                }
              }
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async addedValues() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(addedValuesAndProducts).pipe(
            switchMap(async ({ addedValuesState, availableProducts }) => {
              const families: SelectedAddedValuesRms[] = [];
              const productCode = _safe(() => recipe[SpecFieldNames.PRODUCT_SELECTION]);
              const product = availableProducts.find(
                (products) => products.comm_product.comm_product.replace(/^0+/, '') === productCode,
              );
              const availableAddedValue = addedValuesState.availableAddedValues[productCode];
              const addedValue = availableAddedValue[0];
              const baseName = 'addedProduct';

              for (const name of Object.keys(recipe)) {
                if (name.substr(0, baseName.length) === baseName && _safe(() => recipe['addedProduct0'])) {
                  const valueAddedId: string = _safe(() => recipe[name]);
                  const addedValueSelected = addedValue.addedValues.find((item) => item.family === valueAddedId);
                  const value = {
                    prod: addedValueSelected && addedValueSelected.family,
                    desc: addedValueSelected && addedValueSelected.desc,
                    contractItem: addedValueSelected && (await this.addedValueContractItems(addedValueSelected.family)),
                  };
                  if (!!addedValueSelected && value) {
                    families.push(value);
                  }
                }
              }

              return { families, product };
            }),
            switchMap(async ({ families, product }) => {
              let preprendedVaps: SelectedAddedValuesRms[] = [];
              const addedProductByUsser = _safe(() => recipe['addedProduct0']);
              const country = await this.getCountryCode();
              if (
                product.comm_product.type === '0' &&
                addedProductByUsser !== undefined &&
                country === COUNTRY_CODE.MEXICO
              ) {
                const additiveLineCode = product.comm_product.additive_line;
                const additionLineCode = product.comm_product.addition_line;
                const _defaultVaps = findAddedValuesFromIds(
                  [additiveLineCode, additionLineCode],
                  product.comm_product.productRecommendation,
                ).map(async (vap) => {
                  return {
                    prod: vap.family,
                    desc: vap.desc,
                    contractItem: await this.addedValueContractItems(vap.family),
                  };
                });

                const defaultVaps = await Promise.all(_defaultVaps);

                // Order it (Additive 1st, Addition 2nd)
                preprendedVaps = [
                  defaultVaps.find((valueAdded) => valueAdded.prod === additiveLineCode),
                  defaultVaps.find((valueAdded) => valueAdded.prod === additionLineCode),
                ];
              }

              // @ts-ignore
              return [...preprendedVaps, ...families].filter((_) => !!_);
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async addedValueContractItems(family: string) {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const contractCode = _safe(() => recipe[SpecFieldNames.CONTRACT_SELECT]);
          const productCode = _safe(() => recipe[SpecFieldNames.PRODUCT_SELECTION]);
          return this.store.select(getAvailableContracts).pipe(
            map((availableContracts) => availableContracts[productCode]),
            map((contracts) => {
              const contractItems = _safe(
                () =>
                  contracts.find((contract) => contract.documentCode.replace(/^0+/, '') === contractCode)
                    .refDocumentItems,
              );
              const itemPosition = _safe(() =>
                contractItems
                  .find((contractItem) => contractItem.productCode === family)
                  .refDocumentItemNumber.toString(),
              );
              return itemPosition || '';
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async technicalProduct() {
    const technicalProduct = await this.lastTechnicalProduct();
    return technicalProduct && technicalProduct.tech_prod;
  }

  private async lastTechnicalProduct() {
    const country = await this.getCountryCode();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const productCode = _safe(() => recipe[SpecFieldNames.PRODUCT_SELECTION]);
          return this.store.select(productAddedValues(productCode)).pipe(
            filter((availables) => _safe(() => availables.every((av) => !av.loading))),
            map((availables) =>
              availables.filter((available) => {
                if (available.addedValues === null && available.technicalProduct === null) {
                  return false;
                } else {
                  return true;
                }
              }),
            ),
            map((availables) => availables[availables.length - 1]),
            map((last) => last.technicalProduct),
            map((technicals) => {
              const unloadTypeValue = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
              const hasPump = isPump(unloadTypeValue);
              if (country === COUNTRY_CODE.USA) {
                return technicals && technicals[0];
              }

              // If the user selects Pump, needs a pumpable technical product.
              if (!!hasPump) {
                return technicals && technicals.find((technical) => technical.is_pumpable === hasPump);
              }
              // If the user does not selects Pump,
              // Select the first technical product as default regardless the unload type.
              return technicals && technicals.length && technicals[0];
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getQuantity() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => parseFloat(recipe[SpecFieldNames.QUANTITY]).toString()),
        take(1),
      )
      .toPromise();
  }

  private async getScheduleDate() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => this.getDateTime(availableSlot, recipe).date),
        take(1),
      )
      .toPromise();
  }

  private async getScheduleTime() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => this.getDateTime(availableSlot, recipe).time),
        take(1),
      )
      .toPromise();
  }

  private async getRequestedDate() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          if (_safe(() => recipe[SpecFieldNames.WAITING_LIST_MOMENT]) !== undefined) {
            return moment(_safe(() => recipe[SpecFieldNames.WAITING_LIST_MOMENT])).format('YYYY-MM-DD');
          } else if (_safe(() => _safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT])) !== undefined) {
            return moment(_safe(() => _safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT]))).format('YYYY-MM-DD');
          } else {
            return this.getDateTime(availableSlot, recipe).date;
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getRequestedTime() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          if (_safe(() => recipe[SpecFieldNames.WAITING_LIST_MOMENT]) !== undefined) {
            return moment(_safe(() => recipe[SpecFieldNames.WAITING_LIST_MOMENT])).format('HH:mm');
          } else if (_safe(() => _safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT])) !== undefined) {
            return moment(_safe(() => _safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT]))).format('HH:mm');
          } else {
            return this.getDateTime(availableSlot, recipe).time;
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getPipesQuantity() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          if (_safe(() => recipe[SpecFieldNames.PUMP]) !== undefined) {
            return _safe(() => recipe[SpecFieldNames.PIPE_QUANTITY]);
          } else {
            return 0;
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getHosesQuantity() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          if (_safe(() => recipe[SpecFieldNames.PUMP]) !== undefined) {
            return _safe(() => recipe[SpecFieldNames.HOSES]);
          } else {
            return 0;
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getAdjustmentQuantity() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          if (_safe(() => recipe[SpecFieldNames.ADJUSTMENT]) === true) {
            return calculateAdjustmentQuantity(
              parseFloat(_safe(() => recipe[SpecFieldNames.QUANTITY])),
              Number(this.translateService.instant('global.decimalNumbers')),
            );
          } else {
            return '';
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getCleanUp() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          if (_safe(() => recipe[SpecFieldNames.CLEAN_UP]) === true) {
            return Mark.MARK_AS_YES;
          } else {
            return '';
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getWaitingListMark() {
    const waitingListDefaulted = await this.getSlotWaitingListDefaulted();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          if (_safe(() => recipe[SpecFieldNames.WAITING_LIST_MOMENT]) !== undefined && !waitingListDefaulted) {
            return Mark.MARK_AS_YES;
          } else {
            return '';
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getDocumentType() {
    return this.store
      .select(getSummaryPage)
      .pipe(
        map((state) => {
          if (_safe(() => !!state.specialInvoice)) {
            return OrderTypeCodesEnum.RmsSpecialInvoiceOrder;
          } else {
            return '';
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getLanguage() {
    return this.store
      .select(getLanguage)
      .pipe(
        map((language) => {
          const languageCode = language.split('_');
          return languageCode[0].toLocaleUpperCase();
        }),
        take(1),
      )
      .toPromise();
  }

  private async getModifyReasonCode() {
    return this.store
      .select(getModifyReasonCode)
      .pipe(
        map((reasonCode) => {
          return reasonCode ? reasonCode : null;
        }),
        take(1),
      )
      .toPromise();
  }

  private async getModifyReasonComment() {
    return this.store
      .select(getModifyReasonComment)
      .pipe(
        map((comment) => {
          return comment ? comment : '';
        }),
        take(1),
      )
      .toPromise();
  }

  private async isBigOrder() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getFirstCalendar).pipe(
            take(1),
            map((state) => {
              const quantity = recipe[SpecFieldNames.QUANTITY];
              const bigOrderQuantity = _safe(() => state.bigOrder.quantity);
              return quantity > bigOrderQuantity ? true : false;
            }),
          );
        }),
      )
      .toPromise();
  }

  private async isExceededLinearMeters() {
    const maxLinearMeters = await this.getMaxLinearMeters();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getFirstCalendar).pipe(
            take(1),
            map((calendar) => {
              if (calendar?.pump?.flagLinearMeters && !!recipe[SpecFieldNames.PUMP]) {
                const linearMeters = +recipe[SpecFieldNames.LINEAR_METERS];
                return linearMeters >= maxLinearMeters;
              } else {
                return false;
              }
            }),
          );
        }),
      )
      .toPromise();
  }

  private getMaxLinearMeters() {
    return this.configService
      .getConfig()
      .pipe(
        take(1),
        map((config) => config.maxLinearMeters || 0),
      )
      .toPromise();
  }

  private async getDigitalConfirmationTicket() {
    return this.store
      .select(getSlotsResponse)
      .pipe(
        map((slots) => {
          return _safe(() => slots.header.prefix) !== undefined ? slots.header.prefix : '';
        }),
        take(1),
      )
      .toPromise();
  }

  private async getScheduleAvailability() {
    return this.store
      .select(getSlotsAvailability)
      .pipe(
        take(1),
        map((scheduleAvailability) => scheduleAvailability),
      )
      .toPromise();
  }

  private async getDigitalConfirmationRunTime() {
    return this.store
      .select(getSlotsResponse)
      .pipe(
        map((slots) => {
          return _safe(() => slots.header.runtimes.total) !== undefined ? slots.header.runtimes.total : '';
        }),
        take(1),
      )
      .toPromise();
  }

  private async getOrderCategory() {
    const comments = await this.getSpecialInstructions();
    const bigOrder = await this.isBigOrder();
    const isExceededLinearMeters = await this.isExceededLinearMeters();
    const scheduleAvailability = await this.getScheduleAvailability();
    const foreignJobsites = await this.isForeignJobsites();
    const isModifyFlag = await this.getRmsModifyFlag();

    if (!!bigOrder) {
      return OrderCategory.BIG_ORDER;
    } else if (!!foreignJobsites) {
      return OrderCategory.FOREIGN_JOBSITES;
    } else if (isExceededLinearMeters) {
      return OrderStatusEnum.NoAvailability;
    } else if (!isModifyFlag && !scheduleAvailability) {
      return OrderStatusEnum.NoAvailability;
    } else if (comments !== '') {
      return OrderCategory.COMMENTS;
    } else {
      return '';
    }
  }

  private async getRmsModifyFlag() {
    return this.store
      .select(getRmsModifyFlag)
      .pipe(
        take(1),
        map((state) => state.modifyFlag),
      )
      .toPromise();
  }

  private async getOrderStatus() {
    const isExceededLinearMeters = await this.isExceededLinearMeters();
    if (isExceededLinearMeters) {
      return OrderStatusEnum.ToBeConfirmed;
    }

    const bigOrder = await this.isBigOrder();
    const scheduleAvailability = await this.getScheduleAvailability();
    const ForeignJobsites = await this.isForeignJobsites();
    const isModifyFlag = await this.getRmsModifyFlag();

    if (!!bigOrder || (!scheduleAvailability && !isModifyFlag) || !!ForeignJobsites) {
      return OrderStatusEnum.ToBeConfirmed;
    } else {
      return '';
    }
  }

  private async defaultPlant() {
    return this.store
      .select(getFirstCalendar)
      .pipe(
        take(1),
        map((state) => state.bigOrder.plantCode),
      )
      .toPromise();
  }

  private generateMagicNumber() {
    return moment().format('YYYYMMDDhhmmss') + Math.floor(10000 + Math.random() * 90000).toString();
  }

  private async isForeignJobsites() {
    return this.store
      .select(getSlotError)
      .pipe(
        take(1),
        map((error) => {
          if (error && error.error.reasonCode === ErrorCode.FOREIGN_JOBSITES) {
            return true;
          } else {
            return false;
          }
        }),
      )
      .toPromise();
  }

  private async getSlump() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getFirstCalendar).pipe(
            map((state) => {
              const slump = recipe[SpecFieldNames.SLUMP_PRODUCT];
              return slump !== (null || undefined) ? Number(slump) : 0;
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private getUnloadTime(catalogs: Catalog[], recipe: KeyValues) {
    const unloadTimeCodeSelected = _safe(() => recipe[SpecFieldNames.DISCHARGE_TIME]);
    const UnloadTimeCatalog = _safe(
      () => catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.DISCHARGE_TIME).entries,
    );
    const unloadTimeParameters = _safe(
      () => UnloadTimeCatalog.find((entry) => entry.entryCode === unloadTimeCodeSelected).additionalParameters,
    );
    const unloadTime = Number(
      _safe(() => unloadTimeParameters.find((item) => item.key === ADDITIONAL_PARAMETERS.TIME_QUANTITY).value),
    );

    return unloadTime;
  }

  private defaultUnloadObject(catalogs: Catalog[], recipe: KeyValues) {
    const unloadTypeCodeSelected = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
    const unloadTypeCatalog = _safe(
      () => catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.UNLOAD_TYPE).entries,
    );
    const unloadTypeSelected = _safe(() =>
      unloadTypeCatalog.find((entry) => entry.entryCode === unloadTypeCodeSelected),
    );

    const unloadTime = this.getUnloadTime(catalogs, recipe);
    const unloadTypeExtendedDescription = _safe(
      () =>
        unloadTypeSelected.additionalParameters.find((item) => item.key === ADDITIONAL_PARAMETERS.RMS_UNLOAD_TYPE_CODE)
          .value,
    );
    return {
      id: Number(unloadTypeSelected.entryId),
      description: unloadTypeExtendedDescription.toUpperCase(),
      unloadTime: unloadTime,
    };
  }

  private makePumpSizeCode(catalogs: Catalog[], recipe: KeyValues) {
    const pumpCapatityCodeSelected = _safe(() => recipe[SpecFieldNames.PUMP]);
    const pumpCapacityCatalog = _safe(() =>
      catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.PUMP_CAPACITY),
    );
    const pumpCapacityAdditionalParameters = _safe(
      () =>
        pumpCapacityCatalog.entries.find((entry) => entry.entryCode === pumpCapatityCodeSelected).additionalParameters,
    );
    const pumpSizeCode = Number(
      _safe(
        () => pumpCapacityAdditionalParameters.find((item) => item.key === ADDITIONAL_PARAMETERS.PUMP_SIZE_CODE).value,
      ),
    );
    if (_safe(() => recipe[SpecFieldNames.PUMP]) !== undefined) {
      return pumpSizeCode;
    } else {
      return '';
    }
  }

  /**
   * Returns boom pump specific size if defined by the user
   * @returns
   */
  private async getReqPumpType(): Promise<number> {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          // Only boom pump (Bomba pluma) has this case
          if (recipe[SpecFieldNames.UNLOAD_TYPE] === BOOM_PUMP) {
            return this.store.select(getFirstCalendar).pipe(
              map((calendar) => {
                const placingBoomPump = calendar.pump.unloadMethods.find(
                  (method) => method.unloadMethodCode === BOOM_PUMP_CODE,
                );
                const selectedPump = placingBoomPump.listPumpPen.find(
                  (item) => item.pumpSizeCode === recipe[SpecFieldNames.PUMP],
                );

                if (selectedPump && selectedPump.flagSpecificSize) {
                  return +recipe[SpecFieldNames.PUMP_LENGTH];
                } else {
                  return undefined;
                }
              }),
              take(1),
            );
          } else {
            return of(undefined);
          }
        }),
      )
      .toPromise();
  }

  private async getLinearMeters(): Promise<number> {
    const recipe = await this.recipe();
    return this.store
      .select(getFirstCalendar)
      .pipe(
        take(1),
        map((calendar) => {
          const unloadTypeValue = _safe(() => recipe[SpecFieldNames.UNLOAD_TYPE]);
          const hasPump = isPump(unloadTypeValue);
          if (_safe(() => calendar.pump.flagLinearMeters, false) && hasPump) {
            return recipe[SpecFieldNames.LINEAR_METERS];
          } else {
            return undefined;
          }
        }),
      )
      .toPromise();
  }
}
